import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments';
import {
  AdminCommissionDashboard,
  AdminCommissionRequest,
  AdminCommissionRecordDetails,
  AdminCommissionSalespersonDetails,
  AdminOption,
  AdminPrintLab,
  AdminPrintLabAssignmentRule,
  AdminPrintLabAssignmentRuleView,
  AdminPrintLabInput,
  AdminUserCustomFilter,
  AdminUserDetails,
  AdminUserFilter,
  AdminUserInput,
  AdminUserPage,
} from '../models/admin';
import { OrderListCustomFilter, OrderListFilter, OrderListItemPage } from '../models/treatment';
import { SKIP_ADMIN_CONNECTION_CONTEXT } from '../interceptors/admin-connection-context.token';

/** Provides the administration contracts without changing existing user-account APIs. */
@Injectable({ providedIn: 'root' })
export class AdminApiClient {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly apiBaseUrl: string = environment.apiBaseUrl.replace(/\/$/, '');
  private readonly baseUrl: string = `${this.apiBaseUrl}/admin`;

  /** Retrieves persisted commission totals, grouped and paginated by salesperson. */
  public getCommissionDashboard(request: AdminCommissionRequest): Observable<AdminCommissionDashboard> {
    return this.http.get<AdminCommissionDashboard>(`${this.baseUrl}/commissions/dashboard`, {
      params: this.commissionParams(request),
    });
  }

  /** Retrieves the awards and breakdown for one named or unassigned salesperson. */
  public getCommissionSalespersonDetails(
    request: AdminCommissionRequest
  ): Observable<AdminCommissionSalespersonDetails> {
    return this.http.get<AdminCommissionSalespersonDetails>(`${this.baseUrl}/commissions/salesperson-details`, {
      params: this.commissionParams(request),
    });
  }

  /** Retrieves every payment contributing to an active award without clipping to report dates. */
  public getCommissionRecordDetails(recordId: number): Observable<AdminCommissionRecordDetails> {
    return this.http.get<AdminCommissionRecordDetails>(`${this.baseUrl}/commissions/records/${recordId}`);
  }

  private commissionParams(request: AdminCommissionRequest): HttpParams {
    let params: HttpParams = new HttpParams().set('page', request.page).set('pageSize', request.pageSize);
    if (request.from != null) {
      params = params.set('from', request.from);
    }
    if (request.to != null) {
      params = params.set('to', request.to);
    }
    if (request.orderCategory != null) {
      params = params.set('orderCategory', request.orderCategory);
    }
    if (request.salespersonName != null) {
      params = params.set('salespersonName', request.salespersonName);
    }
    if (request.unassignedSalesperson === true) {
      params = params.set('unassignedSalesperson', true);
    }
    return params;
  }

  /** Lists every salesperson with active commissions; null represents unassigned awards. */
  public getCommissionSalespeople(): Observable<(string | null)[]> {
    return this.http.get<(string | null)[]>(`${this.baseUrl}/commissions/salespeople`);
  }

  /** Returns twenty users from a one-based page, including inactive accounts. */
  public getUsers(page: number, filter: string, customFilter: AdminUserCustomFilter): Observable<AdminUserPage> {
    const request: AdminUserFilter = {
      page,
      rowsOnPage: 20,
      orderBy: 'LastLoginDate desc',
      filter: filter.trim(),
      onlyActive: false,
      customFilter,
    };
    return this.http.post<AdminUserPage>(`${this.baseUrl}/users/page`, request);
  }

  /** Returns twenty orders from a one-based page, searched by user name or email. */
  public getOrders(page: number, filter: string, customFilter: OrderListCustomFilter): Observable<OrderListItemPage> {
    const request: OrderListFilter = {
      page,
      rowsOnPage: 20,
      orderBy: 'ModifiedDate desc',
      filter: filter.trim(),
      customFilter,
    };
    return this.http.post<OrderListItemPage>(`${this.baseUrl}/orders/page`, request);
  }

  /** Assigns an active print lab to a treatment in the current administrator's organization. */
  public updateOrderPrintLab(orderId: string, printLabId: string): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/orders/${encodeURIComponent(orderId)}/print-lab/${encodeURIComponent(printLabId)}`,
      null
    );
  }

  /** Retrieves details before editing an account. */
  public getUser(id: string, useOriginalAdminContext: boolean = false): Observable<AdminUserDetails> {
    const url: string = `${this.baseUrl}/users/${encodeURIComponent(id)}`;
    return useOriginalAdminContext
      ? this.http.get<AdminUserDetails>(url, { context: this.originalAdminContext() })
      : this.http.get<AdminUserDetails>(url);
  }

  /** Retrieves roles supported by the administration service. */
  public getRoles(useOriginalAdminContext: boolean = false): Observable<AdminOption[]> {
    const url: string = `${this.baseUrl}/roles`;
    return useOriginalAdminContext
      ? this.http.get<AdminOption[]>(url, { context: this.originalAdminContext() })
      : this.http.get<AdminOption[]>(url);
  }

  /** Retrieves offices available to the current administrator. */
  public getOffices(): Observable<AdminOption[]> {
    return this.http.get<AdminOption[]>(`${this.baseUrl}/offices`);
  }

  /** Retrieves labs available to the current administrator. */
  public getLabs(): Observable<AdminOption[]> {
    return this.http.get<AdminOption[]>(`${this.baseUrl}/labs`);
  }

  /** Retrieves organizations available to the current administrator. */
  public getOrganizations(): Observable<AdminOption[]> {
    return this.http.get<AdminOption[]>(`${this.baseUrl}/organizations`);
  }

  /** Retrieves every print lab in the current administrator's organization. */
  public getPrintLabs(): Observable<AdminPrintLab[]> {
    return this.http.get<AdminPrintLab[]>(`${this.baseUrl}/print-labs`);
  }

  /** Creates or updates a print lab without exposing organization selection to the client. */
  public savePrintLab(model: AdminPrintLabInput): Observable<AdminPrintLab> {
    return this.http.post<AdminPrintLab>(`${this.baseUrl}/print-labs`, model);
  }

  /** Toggles whether a print lab can receive future assignments. */
  public changePrintLabStatus(id: string): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/print-labs/${encodeURIComponent(id)}/status`, null);
  }

  /** Retrieves the current mappings for the four managed treatment types. */
  public getPrintLabAssignmentRules(): Observable<AdminPrintLabAssignmentRuleView[]> {
    return this.http.get<AdminPrintLabAssignmentRuleView[]>(`${this.baseUrl}/print-lab-assignment-rules`);
  }

  /** Saves the complete treatment-type to print-lab assignment set. */
  public savePrintLabAssignmentRules(models: AdminPrintLabAssignmentRule[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/print-lab-assignment-rules`, models);
  }

  /** Creates an account; the backend generates its initial password and sends an email. */
  public createUser(model: AdminUserInput): Observable<string> {
    return this.http.post<string>(`${this.baseUrl}/users`, model);
  }

  /** Saves user details and compatible role assignments. */
  public updateUser(id: string, model: AdminUserInput): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/users/${encodeURIComponent(id)}`, model);
  }

  /** Changes a password independently of profile updates. */
  public changePassword(
    id: string,
    password: string,
    confirmPassword: string,
    oldPassword?: string | null
  ): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/users/${encodeURIComponent(id)}/password`, {
      password,
      confirmPassword,
      ...(oldPassword != null ? { oldPassword } : {}),
    });
  }

  /** Sets the requested active state using the API's boolean body contract. */
  public setActive(id: string, isActive: boolean): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/users/${encodeURIComponent(id)}/status`, isActive);
  }

  private originalAdminContext(): HttpContext {
    return new HttpContext().set(SKIP_ADMIN_CONNECTION_CONTEXT, true);
  }
}
